import { useCallback, useMemo } from 'react'
import { ListboxGroup } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { Importer } from '@/features/importers/hooks/importers'
import type { ImportedGood } from '@/features/cbam-reporting/hooks/importedGoods'
import { getBatchRemoveActionsAvailability } from '@/shared/utils/batchUpdateSelectionRules'
import type { BulkUpdateImporterFormData } from './BulkUpdateImporterForm.schema'
import { EMPTY_VALUE } from './BulkUpdateImporterForm.schema'

type BulkUpdateImporterFormProps = {
  data: BulkUpdateImporterFormData
  onChange: (data: BulkUpdateImporterFormData) => void
  importers: Importer[]
  selectedGoods: Pick<ImportedGood, 'importerId'>[]
}

export default function BulkUpdateImporterForm({
  data,
  onChange,
  importers,
  selectedGoods
}: BulkUpdateImporterFormProps) {
  const { t } = useI18n()
  const removeActionsAvailability = useMemo(
    () => getBatchRemoveActionsAvailability(selectedGoods),
    [selectedGoods]
  )

  const handleFieldChange = useCallback(
    (value: string) => {
      onChange({ ...data, importerId: value })
    },
    [onChange, data]
  )

  const importerOptions = useMemo(
    () => [
      ...(removeActionsAvailability.canRemoveImporter
        ? [{ value: EMPTY_VALUE, label: t('cbamReporting:bulkUpdate.removeImporter') }]
        : []),
      ...importers.map(importer => ({ value: importer.id, label: importer.name }))
    ],
    [importers, removeActionsAvailability.canRemoveImporter, t]
  )

  return (
    <div className="space-y-4">
      <ListboxGroup
        label={t('cbamReporting:bulkUpdate.fields.importer')}
        value={data.importerId}
        placeholder={t('cbamReporting:bulkUpdate.noChange')}
        options={importerOptions}
        onChange={handleFieldChange}
      />
    </div>
  )
}
